import PropTypes from 'prop-types';
import { Box, Typography, Skeleton, Tooltip, IconButton } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

const subColorMap = {
	down: 'score.down',
	up: 'score.up',
	warn: 'score.warn',
	neutral: 'text.secondary',
};

const KPICard = ({
	label,
	value,
	sub,
	subColor,
	valueColor, // optional MUI color path to override the default value color
	vdr,
	onClick,
	loading,
	variant = 'default',
	children, // display custom graphics or content instead of the default KPI value
	tooltipForSub = '', // display a tooltip with help text next to the sub text
}) => {
	const mode = vdr ? 'vdr' : variant;
	const subColorByMode = {
		default: subColorMap[subColor] || 'text.secondary',
		compact: subColorMap[subColor] || 'text.secondary',
		vdr: 'vdr.text',
	};

	return (
		<Box
			onClick={onClick}
			sx={{
				...cardStyleByMode[mode],
				cursor: mode === 'vdr' || onClick ? 'pointer' : 'default',
			}}
		>
			{label && (
				<Typography
					noWrap={mode === 'compact'}
					title={label}
					sx={{
						fontSize: '11px',
						color: labelColorByMode[mode],
						mb: labelMbByMode[mode],
					}}
				>
					{label}
				</Typography>
			)}
			{children ? (
				// Match box height for custom children with KPI value text for consistent alignment
				<Box sx={childrenBoxSxByMode[mode]}>{children}</Box>
			) : (
				<Typography
					sx={{
						fontSize: valueFontSizeByMode[mode],
						fontWeight: 600,
						color: valueColor || valueColorByMode[mode],
						lineHeight: 1.1,
					}}
				>
					{/* Show a loading skeleton until KPI data is available, preventing placeholder values from flashing in the UI */}
					{loading ? (
						<Skeleton
							data-testid='kpi-skeleton'
							width={70}
							height={skeletonHeightByMode[mode]}
							sx={{ transform: 'none', mb: '5px' }}
						/>
					) : (
						value
					)}
				</Typography>
			)}

			{/* Show sub text with no tooltip (default view) */}
			{sub && !tooltipForSub && (
				<Typography
					sx={{
						fontSize: '11px',
						mt: subMtByMode[mode],
						color: subColorByMode[mode],
						opacity: subOpacityByMode[mode],
					}}
				>
					{/* Show a loading skeleton until KPI data is available, preventing placeholder values from flashing in the UI */}
					{loading ? (
						<Skeleton
							data-testid='kpi-skeleton'
							width={70}
							height={15}
							sx={{ transform: 'none' }}
						/>
					) : (
						sub
					)}
				</Typography>
			)}

			{/* Show sub text with a tooltip */}
			{sub && tooltipForSub && (
				<Box
					display='flex'
					alignItems='center'
					gap={0.5}
					sx={{
						mt: subMtByMode[mode],
						color: subColorByMode[mode],
						opacity: subOpacityByMode[mode],
					}}
				>
					<Typography sx={{ fontSize: 11 }}>{sub}</Typography>

					<Tooltip title={tooltipForSub} disableHoverListener={loading}>
						<IconButton
							size='small'
							aria-label={tooltipForSub}
							data-testid='tooltip-icon'
							sx={{ p: 0 }}
						>
							<InfoOutlined sx={{ fontSize: 12 }} />
						</IconButton>
					</Tooltip>
				</Box>
			)}
		</Box>
	);
};

const cardStyleByMode = {
	default: {
		backgroundColor: 'background.paper',
		border: '0.5px solid',
		borderColor: 'border.light',
		borderRadius: '8px',
		padding: '11px 13px',
	},
	compact: {
		backgroundColor: 'background.subtle',
		border: 'none',
		borderRadius: '7px',
		padding: '9px 11px',
		minWidth: 0,
	},
	vdr: {
		backgroundColor: 'vdr.bg',
		border: '0.5px solid',
		borderColor: 'vdr.border',
		borderRadius: '8px',
		padding: '11px 13px',
		'&:hover': { backgroundColor: 'vdr.hover' },
	},
};

// Match box height for custom children with KPI value text for consistent alignment
const childrenBoxSxByMode = {
	default: {
		height: '22.5px',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
	},
	compact: {
		height: '17.5px',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
	},
	vdr: {
		height: '22.5px',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'center',
	},
};

const labelMbByMode = { default: '1px', compact: '2px', vdr: '1px' };
const labelColorByMode = {
	default: 'text.secondary',
	compact: 'text.secondary',
	vdr: 'vdr.text',
};

const valueFontSizeByMode = { default: '21px', compact: '16px', vdr: '21px' };
const valueColorByMode = {
	default: 'text.primary',
	compact: 'text.primary',
	vdr: 'vdr.text',
};

const skeletonHeightByMode = { default: 22.5, compact: 17.5, vdr: 22.5 };

const subMtByMode = { default: '2px', compact: '1px', vdr: '2px' };
const subOpacityByMode = { default: 1, compact: 1, vdr: 0.7 };

KPICard.propTypes = {
	label: PropTypes.string,
	value: PropTypes.string,
	sub: PropTypes.string,
	tooltipForSub: PropTypes.string,
	subColor: PropTypes.oneOf(['down', 'up', 'warn', 'neutral']),
	valueColor: PropTypes.string,
	vdr: PropTypes.bool,
	onClick: PropTypes.func,
	loading: PropTypes.bool,
	variant: PropTypes.oneOf(['default', 'compact']),
	children: PropTypes.node,
};

export default KPICard;
